import { useEffect, useMemo, useState, type ComponentType } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  BarChart3,
  Banknote,
  Calendar,
  ClipboardCheck,
  FileText,
  GraduationCap,
  MessageSquare,
  Search,
  Settings,
  UserPlus,
  Users,
} from 'lucide-react'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { useToast } from '@/hooks/use-toast'

type CommandResult = {
  id: string
  group: 'Recents' | 'Students' | 'Actions' | 'Pages'
  label: string
  detail?: string
  icon: ComponentType<{ className?: string }>
  run: () => void
}

const pageResults = [
  { id: 'dashboard', label: 'Dashboard', detail: 'Control tower', href: '/app/dashboard', icon: BarChart3 },
  { id: 'students', label: 'Students', detail: 'Roster, fees, ID cards', href: '/app/students', icon: Users },
  { id: 'teachers', label: 'Teachers & Staff', detail: 'Accounts, roles, leave', href: '/app/teachers', icon: GraduationCap },
  { id: 'finance', label: 'Finance', detail: 'Invoices and payments', href: '/app/school-management?tab=fees', icon: Banknote },
  { id: 'payroll', label: 'Payroll', detail: 'Payslips and approvals', href: '/app/school-management?tab=payroll', icon: Banknote },
  { id: 'attendance', label: 'Attendance & Gate', detail: 'QR scan log', href: '/app/attendance', icon: ClipboardCheck },
  { id: 'cafeteria', label: 'Cafeteria', detail: 'Menu and balances', href: '/app/cafeteria', icon: Calendar },
  { id: 'reports', label: 'Analytics & Reports', detail: 'Report cards and exports', href: '/app/report-cards', icon: FileText },
  { id: 'transport', label: 'Transport', detail: 'Routes, vehicles, GPS', href: '/app/school-management?tab=transport', icon: Calendar },
  { id: 'resources', label: 'Resources & DMS', detail: 'Inventory and documents', href: '/app/school-management?tab=documents', icon: FileText },
  { id: 'communication', label: 'Communication', detail: 'Announcements and messages', href: '/app/communication', icon: MessageSquare },
  { id: 'setup', label: 'School Setup', detail: 'Classes, subjects, modules', href: '/app/school-management?tab=setup', icon: Settings },
]

const studentResults = [
  { id: 'student-amina', label: 'Amina Bakary', detail: 'Grade 8A - Partial fees' },
  { id: 'student-david', label: 'David Moukoko', detail: 'Grade 10B - Gate in' },
  { id: 'student-lina', label: 'Lina Tamba', detail: 'Grade 6C - Absent today' },
]

function scoreMatch(query: string, value: string) {
  const normalizedQuery = query.trim().toLowerCase()
  if (!normalizedQuery) return true
  const normalizedValue = value.toLowerCase()
  let cursor = 0
  for (const char of normalizedQuery) {
    cursor = normalizedValue.indexOf(char, cursor)
    if (cursor === -1) return false
    cursor += 1
  }
  return true
}

export function CommandPalette() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [activeIndex, setActiveIndex] = useState(0)

  useEffect(() => {
    const openCommand = () => setOpen(true)
    window.addEventListener('admin:open-command-palette', openCommand)
    return () => window.removeEventListener('admin:open-command-palette', openCommand)
  }, [])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const isTyping = target?.tagName === 'INPUT' || target?.tagName === 'TEXTAREA' || target?.isContentEditable
      const wantsCommand = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k'
      const wantsSlash = event.key === '/' && !isTyping

      if (wantsCommand || wantsSlash) {
        event.preventDefault()
        setOpen(true)
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [])

  const results = useMemo<CommandResult[]>(() => {
    const actions: CommandResult[] = [
      {
        id: 'action-add-student',
        group: query ? 'Actions' : 'Recents',
        label: 'Enrol a student',
        detail: 'Profile, class, fees, transport, cafeteria, ID card',
        icon: UserPlus,
        run: () => {
          window.dispatchEvent(new Event('admin:open-enrol-student'))
          setOpen(false)
        },
      },
      {
        id: 'action-record-payment',
        group: query ? 'Actions' : 'Recents',
        label: 'Record payment',
        detail: 'Open the finance workflow',
        icon: Banknote,
        run: () => {
          navigate('/app/school-management?tab=fees')
          setOpen(false)
        },
      },
      {
        id: 'action-generate-reports',
        group: 'Actions',
        label: 'Generate report cards',
        detail: 'Prepare class report cards',
        icon: FileText,
        run: () => {
          navigate('/app/report-cards')
          toast({ title: 'Report card workflow opened' })
          setOpen(false)
        },
      },
    ]

    const students = studentResults.map<CommandResult>((student) => ({
      ...student,
      group: 'Students',
      icon: Users,
      run: () => {
        navigate('/app/students')
        toast({ title: `${student.label} selected`, description: 'Student detail routing can be connected to the real roster API.' })
        setOpen(false)
      },
    }))

    const pages = pageResults.map<CommandResult>((page) => ({
      ...page,
      group: 'Pages',
      run: () => {
        navigate(page.href)
        setOpen(false)
      },
    }))

    const allResults = [...actions, ...students, ...pages]
    if (!query.trim()) return allResults.filter((item) => item.group === 'Recents').slice(0, 5)

    return allResults.filter((item) => scoreMatch(query, `${item.label} ${item.detail ?? ''} ${item.group}`)).slice(0, 9)
  }, [navigate, query, toast])

  useEffect(() => {
    setActiveIndex(0)
  }, [query, open])

  const groupedResults = results.reduce<Record<string, CommandResult[]>>((groups, result) => {
    groups[result.group] = groups[result.group] ?? []
    groups[result.group].push(result)
    return groups
  }, {})

  const runActive = () => {
    results[activeIndex]?.run()
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="top-[18%] max-w-xl translate-y-0 gap-0 overflow-hidden rounded-xl border border-slate-200 bg-white p-0 shadow-[0_24px_60px_rgba(19,34,91,.28)] dark:border-slate-800 dark:bg-slate-950">
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <div className="flex items-center gap-3 border-b border-slate-200 px-4 py-3 dark:border-slate-800">
          <Search className="h-5 w-5 text-slate-400" />
          <Input
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown') {
                event.preventDefault()
                setActiveIndex((index) => Math.min(index + 1, results.length - 1))
              }
              if (event.key === 'ArrowUp') {
                event.preventDefault()
                setActiveIndex((index) => Math.max(index - 1, 0))
              }
              if (event.key === 'Enter') {
                event.preventDefault()
                runActive()
              }
            }}
            placeholder="Search students, actions, or pages"
            className="h-11 border-0 bg-transparent px-0 text-base shadow-none focus-visible:ring-0"
          />
          <Badge variant="secondary" className="hidden rounded-full bg-slate-100 text-slate-500 sm:inline-flex">
            Cmd K
          </Badge>
        </div>

        <div className="max-h-[420px] overflow-y-auto p-2">
          {results.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-500">No matching command</div>
          ) : (
            Object.entries(groupedResults).map(([group, items]) => (
              <div key={group} className="py-1">
                <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {group}
                </div>
                {items.map((item) => {
                  const index = results.findIndex((result) => result.id === item.id)
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={item.run}
                      onMouseEnter={() => setActiveIndex(index)}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors',
                        activeIndex === index ? 'bg-[#DCE6F7] text-[#073584]' : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-900'
                      )}
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white text-[#0B4DBA] shadow-sm dark:bg-slate-900">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold">{item.label}</span>
                        {item.detail && <span className="block truncate text-xs text-slate-500">{item.detail}</span>}
                      </span>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
